import { StoreShape, ThunkAction } from 'hellospa/redux/types';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import * as types from 'hellospa/page/prep-and-send/data/types';

const configure = async (
  state: StoreShape,
  workflow: types.Workflow,
): Promise<types.Workflow> => {
  const pages = await Promise.all(workflow.pages.map(async (page) => ({
    ...page,
    text: page.component.getText(workflow, state),
  })));

  return {
    ...workflow,
    pages,
  };
};

export const workflowOnSave = (
  actions: (() => ThunkAction<void>)[],
): ThunkAction<void | Promise<unknown>> => async (dispatch) => {
  for (let i = 0; i < actions.length; i++) {
    // eslint-disable-next-line no-await-in-loop
    await dispatch(actions[i]());
  }
};

/**
 * Changes workflow. Strongly suggested to await this
 */
export const setWorkflow = (
  workflow: types.Workflow,
): ThunkAction<void> => async (dispatch, getState) => {
  const payload = await configure(getState(), workflow);
  await dispatch({
    type: types.Actions.SetWorkflow,
    payload,
  });
  const { currentPath } = selectors.getMeta(getState());
  const pathIndex = payload.pages.findIndex((page) => page.path === currentPath);
  const newPath = pathIndex >= 0
    ? payload.pages[pathIndex].path
    : payload.pages[0].path;
  /**
   * If this is a fresh init, currentPath will be empty.
   * Set it to the first Page in Workflow
   */
  if (currentPath !== newPath) {
    await dispatch({
      type: types.Actions.UpdateMeta,
      payload: {
        currentPath: newPath,
      },
    });
  }
};

export const nextPageInWorkflow = (): ThunkAction<void> => async (
  dispatch,
  getState,
) => {
  const workflow = await configure(getState(), selectors.getWorkflow(getState()));
  const currentPageIndex = selectors.getCurrentWorkflowPageIndex(getState());

  dispatch({
    type: types.Actions.SetWorkflow,
    payload: workflow,
  });

  // If there is no next step, do not change history
  if (workflow.pages[currentPageIndex + 1] != null) {
    dispatch({
      type: types.Actions.UpdateMeta,
      payload: {
        currentPath: workflow.pages[currentPageIndex + 1].path,
      },
    });
  }
};

export const previousPageInWorkflow = (): ThunkAction<void> => async (
  dispatch,
  getState,
) => {
  const workflow = await configure(getState(), selectors.getWorkflow(getState()));
  const currentPageIndex = selectors.getCurrentWorkflowPageIndex(getState());

  dispatch({
    type: types.Actions.SetWorkflow,
    payload: workflow,
  });

  // Already on first page of workflow
  if (currentPageIndex === 0) {
    return;
  }

  await dispatch({
    type: types.Actions.UpdateMeta,
    payload: {
      currentPath: workflow.pages[currentPageIndex - 1].path,
    },
  });
};

export const changeWorkflowFromFiles = (
): ThunkAction<void> => async (dispatch, getState) => {
  if (!selectors.allowWorkflowChange(getState())) {
    return;
  }

  const count = {
    [types.UserFileTypes.External]: 0,
    [types.UserFileTypes.Template]: 0,
    [types.UserFileTypes.Upload]: 0,
    [types.UserFileTypes.Signer]: 0,
  };
  selectors.getFiles(getState()).forEach((file) => {
    count[file.type]++;
  });

  // No Templates attached - either only direct uploads or from external
  if (count[types.UserFileTypes.Template] === 0) {
    await dispatch(setWorkflow(types.workflows.SignatureRequestDocument));
    return;
  }

  // No Upload or External files attached
  if (count[types.UserFileTypes.Upload] === 0
    && count[types.UserFileTypes.External] === 0
  ) {
    await dispatch(setWorkflow(types.workflows.SignatureRequestTemplate));
    return;
  }

  // Both Uploads/External and Templates attached
  await dispatch(setWorkflow(types.workflows.SignatureRequestTemplateAndDocument));
};

export const changeWorkflowDeepIntegration = (
): ThunkAction<void> => async (dispatch, getState) => {
  if (!selectors.allowWorkflowChange(getState())) {
    return;
  }

  if (selectors.isMeOnly(getState())) {
    await dispatch(setWorkflow(types.workflows.DeepIntegrationSelfSign));
    return;
  }

  if (selectors.isEmbeddedTemplate(getState())) {
    await dispatch(setWorkflow(types.workflows.DeepIntegrationTemplate));
    return;
  }

  // Default to DeepIntegration
  await dispatch(setWorkflow(types.workflows.DeepIntegration));
};

export const changeWorkflowUseTemplate = (
): ThunkAction<void> => async (dispatch, getState) => {
  if (selectors.getAllMergeFields(getState()).length) {
    await dispatch(setWorkflow(types.workflows.DeepIntegrationUseTemplatesMergeFields));
    return;
  }

  await dispatch(setWorkflow(types.workflows.DeepIntegrationUseTemplate));
};

export const changeWorkFlowIntegrationSelfSign = (
): ThunkAction<void> => async (dispatch, getState) => {
  if (selectors.isMeOnly(getState())) {
    await dispatch(setWorkflow(types.workflows.IntegrationSelfSign));
    return;
  }

  // Default to Integration
  await dispatch(setWorkflow(types.workflows.Integration));
};
